import { Badge, Box, RadioGroup, Table } from '@palmetto/palmetto-components';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { PaymentPlan, NonPlanPaymentEvents, PayoutEventMap, ProgramType } from 'types';

interface PaymentPlanTableProps {
  paymentPlans: Omit<PaymentPlan, 'id'>[];
  programType: ProgramType;
  selectedPaymentPlanId?: string;
  selectPaymentPlan?(id: string): void;
  paymentPlanClickable?: boolean;
  showFirstColumn?: boolean;
}

const getPaymentScheduleColumns = (
  programType: ProgramType,
  selectedPaymentPlanId: string | undefined,
  selectPaymentPlan?: (id: string) => void,
  paymentPlanClickable?: boolean,
  showFirstColumn?: boolean,
) => {
  const baseColumns = showFirstColumn
    ? [
        {
          heading: '',
          dataKey: 'name',
          render: (cell?: any, row?: any) => (
            <Box fontWeight="medium" as="span" display="inline">
              {paymentPlanClickable ? (
                <Link to={`/admin/payouts/${programType}/payment-plans/${row.id}`}>{row.name}</Link>
              ) : (
                cell
              )}
            </Box>
          ),
        },
      ]
    : [];

  const dynamicColumns = PayoutEventMap[programType]
    .filter((entry) => !NonPlanPaymentEvents.includes(entry.event))
    .toSorted((a, b) => a.order - b.order)
    .map((entry) => ({
      heading: entry?.name,
      dataKey: entry.event,
      render: (_cell?: any, row?: any, _rowIndex?: number) => {
        const valueEntry = row.paymentSchedule?.find((event: any) => event.event === entry.event);
        return (
          <Box direction="row" alignItems="center" childGap="xs">
            <Box fontWeight="medium" as="span" display="inline">
              {valueEntry?.paymentPercent ? `${valueEntry.paymentPercent * 100}%` : '-'}
            </Box>
            {valueEntry?.paymentPercent && valueEntry.pausedDays ? (
              <Badge message={`${valueEntry.pausedDays}d hold`} size="sm" variant="primary" />
            ) : null}
          </Box>
        );
      },
    }));

  if (selectPaymentPlan) {
    baseColumns.unshift({
      heading: '',
      dataKey: 'id',
      render: (_cell?: any, row?: any) =>
        row ? (
          <RadioGroup
            value={selectedPaymentPlanId}
            name="selectedPaymentPlan"
            onChange={() => selectPaymentPlan(row.id)}
            options={[{ id: row.id, value: row.id, label: '' }]}
          />
        ) : (
          <></>
        ),
    });
  }

  return [...baseColumns, ...dynamicColumns];
};

export const PaymentPlanTable = ({
  paymentPlans,
  programType,
  selectedPaymentPlanId,
  selectPaymentPlan,
  paymentPlanClickable,
  showFirstColumn = true,
}: PaymentPlanTableProps) => {
  const columns = useMemo(
    () =>
      getPaymentScheduleColumns(
        programType,
        selectedPaymentPlanId,
        selectPaymentPlan,
        paymentPlanClickable,
        showFirstColumn,
      ),
    [programType, selectedPaymentPlanId, selectPaymentPlan, showFirstColumn, paymentPlanClickable],
  );
  return paymentPlans ? <Table rowKey="id" columns={columns} rows={paymentPlans} /> : null;
};
